import React, { useMemo, useRef, useState } from 'react';
import InfoTooltip from '../../common/InfoTooltip';
import PatientLimitContactModal from './PatientLimitContactModal';
import PatientLimitDropJustificationModal from './PatientLimitDropJustificationModal';
import PatientLimitIncreaseBlockedModal from './PatientLimitIncreaseBlockedModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import { PENDING_DISCHARGED_STATUS_ID } from '../../../utils/psychologistStatus';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import useToast from '@/hooks/useToast';

interface PatientLimitFieldProps {
  /** Valor draft actual (lo que la psicóloga ve y puede modificar) */
  value: number;
  /** Límite interno (process_limit). null mientras carga / si no está disponible. */
  processLimit: number | null;
  /** Cantidad de procesos vigentes (active + retention). null mientras carga / si falla. */
  currentAssignedProcesses: number | null;
  /** Llamado cuando el draft válido cambia */
  onChange: (next: number) => void;
  /** True cuando el draft difiere del valor guardado en backend (cambio pendiente) */
  isModified: boolean;
  /** Necesario para persistir la justificación de baja de preferencia. null si aún no se conoce. */
  psychologistId: string | null;
  /** Límite interno configurado por Psi Mammoliti. Si value > internalLimit se muestra advertencia. */
  internalLimit: number | null;
}

const SELECT_MIN = 5;
const SELECT_MAX = 40;
const SOFT_FLOOR = 10;
/** A partir de esta baja (30% o más) respecto del límite interno, no se puede autogestionar. */
const DROP_FRICTION_THRESHOLD = 0.30;

type DropDecision = 'none' | 'selfService' | 'contactTeam';
type ContactReason = 'floor' | 'significantDrop';

/** Mínimo realmente permitido para self-service */
function computeMinAllowed(currentAssignedProcesses: number | null): number {
  return Math.max(SOFT_FLOOR, currentAssignedProcesses ?? 0);
}

export const PatientLimitField: React.FC<PatientLimitFieldProps> = ({
  value,
  processLimit,
  currentAssignedProcesses,
  onChange,
  isModified,
  psychologistId,
  internalLimit,
}) => {
  const { psychologist } = usePsychologistContext();
  const { showError } = useToast();
  const minAllowed = computeMinAllowed(currentAssignedProcesses);
  const lastValidRef = useRef<number>(value);

  const [modalState, setModalState] = useState<{ open: boolean; attempted: number; reason: ContactReason }>(
    { open: false, attempted: 0, reason: 'floor' },
  );
  const [dropModalState, setDropModalState] = useState<{ open: boolean; newValue: number | null }>(
    { open: false, newValue: null },
  );
  const [submittingJustification, setSubmittingJustification] = useState(false);
  const [increaseBlockedOpen, setIncreaseBlockedOpen] = useState(false);

  const options = useMemo(() => {
    const arr: number[] = [];
    for (let i = SELECT_MIN; i <= SELECT_MAX; i += 1) arr.push(i);
    return arr;
  }, []);

  const isPendingDischarged = psychologist?.statusId === PENDING_DISCHARGED_STATUS_ID;
  /**
   * Regla de fricción al subir la preferencia para psicólogas en pending_discharged: no aplica a
   * N/NZ porque para ellas toda la sección ya está oculta más abajo, pero se deja explícito acá
   * para no depender implícitamente de ese early return.
   */
  const isPendingDischargeIncreaseBlocked = isPendingDischarged
    && psychologist?.rankingCategory !== 'N'
    && psychologist?.rankingCategory !== 'NZ';

  /**
   * Regla de fricción al bajar la preferencia, siempre calculada contra el límite interno
   * (`processLimit`), nunca contra la preferencia anterior: si el nuevo valor queda por debajo
   * del límite interno, se calcula dropRatio = (processLimit - newValue) / processLimit. Por
   * debajo del 30% de baja es autogestionable (con motivo); 30% o más requiere coordinación con
   * el equipo. Excepción total: si el estado es "pending_discharged" nunca se evalúa esta regla
   * — esto es una regla de backend y no debe insinuarse en el frontend.
   */
  const evaluateDrop = (newValue: number): DropDecision => {
    if (processLimit == null || processLimit <= newValue) return 'none';
    const dropRatio = (processLimit - newValue) / processLimit;
    return dropRatio >= DROP_FRICTION_THRESHOLD ? 'contactTeam' : 'selfService';
  };

  const handleSelect = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clampedTop = Math.min(parsed, SELECT_MAX);

    if (isPendingDischargeIncreaseBlocked && clampedTop > value) {
      setIncreaseBlockedOpen(true);
      return;
    }

    if (!isPendingDischarged && clampedTop < minAllowed) {
      setModalState({ open: true, attempted: parsed, reason: 'floor' });
      return;
    }
    if (clampedTop === lastValidRef.current) return;

    if (!isPendingDischarged) {
      const decision = evaluateDrop(clampedTop);
      if (decision === 'contactTeam') {
        setModalState({ open: true, attempted: clampedTop, reason: 'significantDrop' });
        return;
      }
      if (decision === 'selfService') {
        setDropModalState({ open: true, newValue: clampedTop });
        return;
      }
    }

    lastValidRef.current = clampedTop;
    onChange(clampedTop);
  };

  const dropRatio = dropModalState.newValue != null && processLimit
    ? (processLimit - dropModalState.newValue) / processLimit
    : 0;
  const dropPercentage = Math.round(dropRatio * 100);

  const closeDropModal = () => {
    if (submittingJustification) return;
    setDropModalState({ open: false, newValue: null });
  };

  const handleConfirmDropJustification = async (reason: string) => {
    const newValue = dropModalState.newValue;
    if (newValue == null || psychologistId == null) return;

    setSubmittingJustification(true);
    try {
      await apiCoreAdapter.psychologistPatientLimitJustification({
        psychologist_id: psychologistId,
        previous_value: lastValidRef.current,
        new_value: newValue,
        drop_percentage: dropRatio,
        reason,
      });
      lastValidRef.current = newValue;
      onChange(newValue);
      setDropModalState({ open: false, newValue: null });
    } catch {
      showError('No pudimos guardar el motivo. Probá de nuevo.');
    } finally {
      setSubmittingJustification(false);
    }
  };

  // Categorías N/NZ no autogestionan cupo — se oculta toda la sección (incluida, por lo tanto,
  // la fricción/retention por límite interno de arriba, que nunca llega a evaluarse para ellas).
  if (psychologist?.rankingCategory === 'N' || psychologist?.rankingCategory === 'NZ') return null;

  return (
    <div className="tags-container">
      <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
        <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Cantidad de pacientes</p>
        <InfoTooltip
          ariaLabel="Información sobre la cantidad de pacientes"
          text={
            <div className="flex flex-col gap-3">
              <div>
                <p className="font-bold text-gray-400 uppercase tracking-wider m-0 mb-1">Tu preferencia</p>
                <p className="m-0">Indicar tu tope preferido nos ayuda a respetar tu carga ideal. No es una garantía de que recibirás esa cantidad exacta, pero queda registrado y se considera al asignarte nuevos pacientes.</p>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <p className="font-bold text-gray-400 uppercase tracking-wider m-0 mb-1">¿Qué es un proceso activo?</p>
                <p className="m-0">Es un proceso terapéutico que tuvo al menos una sesión en los últimos 30 días — ese es el criterio que usamos para contar cuántos pacientes tenés en curso ahora mismo. No incluye a ex pacientes: si alguien que dejó de atenderse con vos vuelve a buscarte, siempre lo vas a poder recibir, aunque eso implique superar tu preferencia.</p>
              </div>
            </div>
          }
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Tu preferencia */}
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0">
              Tu preferencia
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(value)} onValueChange={handleSelect}>
              <SelectTrigger
                aria-label="Cantidad preferida de pacientes"
                className={`h-10 w-24 rounded-lg border bg-white px-3 text-2xl font-semibold text-gray-700 focus:ring-2 focus:ring-gray-200 ${
                  isModified ? 'border-amber-400 ring-1 ring-amber-200' : 'border-gray-200'
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[220px]" scrollButtonBehavior="slow">
                {options.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-sm py-2">
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isModified && psychologist?.rankingCategory === 'N' && internalLimit != null && value > internalLimit && (
              <InfoTooltip
                iconColor="#d97706"
                ariaLabel="Advertencia sobre el límite de pacientes"
                text={`Vas a recibir hasta ${internalLimit} pacientes, ya que es el límite configurado por Psi Mammoliti.`}
                placement="top"
              />
            )}
          </div>
        </div>

        {/* Procesos activos */}
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 m-0">
            Procesos activos
          </p>
          <div className="h-10 flex items-center">
            <p className="text-2xl font-semibold text-gray-700 m-0 leading-none">
              {currentAssignedProcesses ?? '—'}
            </p>
          </div>
        </div>
      </div>

      <PatientLimitContactModal
        open={modalState.open}
        onClose={() => setModalState({ open: false, attempted: 0, reason: 'floor' })}
        attemptedValue={modalState.attempted}
        minAllowed={minAllowed}
        currentAssignedProcesses={currentAssignedProcesses}
        reason={modalState.reason}
        processLimit={processLimit}
      />

      <PatientLimitDropJustificationModal
        open={dropModalState.open}
        dropPercentage={dropPercentage}
        submitting={submittingJustification}
        onCancel={closeDropModal}
        onConfirm={handleConfirmDropJustification}
      />

      <PatientLimitIncreaseBlockedModal
        open={increaseBlockedOpen}
        onClose={() => setIncreaseBlockedOpen(false)}
      />
    </div>
  );
};

export default PatientLimitField;
